import { IGendecSettingsWire } from "@vmosolutions/oc-interfaces";
import { ThunkAction, ThunkDispatch } from "redux-thunk";
import AmplifyAPIWrapper, { INotifications } from "src/API/AmplifyAPIWrapper";
import { IRootState } from "../../reducers/CombinedReducers";
import { ActionType, IAppAction } from "../Helpers";

/**
 * GenDec Management
 *
 * Hits the dedicated `/gendec/config` endpoints on the Gendecs plugin (internal API).
 * The plugin is only deployed for some customers, so the route may not exist;
 * `AmplifyAPIWrapper` swallows non-2XX responses and returns `null` - that's the
 * availability signal: `state.gendec.loaded && state.gendec.config !== null`.
 *
 * The wire types (`IGendecSettingsWire` / `IGendecRuleWire`) are shared with the
 * backend via `@vmosolutions/oc-interfaces`, so the two sides can't drift.
 */

const CONFIG_PATH = "/gendec/config";

/** Mark/clear the "another operator updated this" flag - drives the banner in both UIs. */
export const setGendecStale = (stale: boolean): IAppAction => ({
  type: ActionType.GENDEC_SET_STALE,
  payload: stale,
});

/**
 * Store a fresh gendec snapshot in Redux
 */
export const setGendecConfig = (config: IGendecSettingsWire | null): IAppAction => ({
  type: ActionType.GENDEC_SET_CONFIG,
  payload: config,
});

/**
 * Fetch the current GenDec config. Returns null when the plugin isn't deployed for
 * this customer (route missing -> non-2XX -> wrapper returns null) or on error.
 */
export const getGendecConfig = async (
  cancelHook?: <T>(p: Promise<T>) => Promise<T>
): Promise<IGendecSettingsWire | null> => {
  return AmplifyAPIWrapper.Get<IGendecSettingsWire>(CONFIG_PATH, undefined, cancelHook);
};

/**
 * Save the full GenDec config. The backend validates, restores rich rule types, and
 * preserves unmanaged fields. Returns the saved config (re-serialized) or null on error.
 */
export const updateGendecConfig = async (
  config: IGendecSettingsWire,
  notifications?: INotifications,
  cancelHook?: <T>(p: Promise<T>) => Promise<T>
): Promise<IGendecSettingsWire | null> => {
  return AmplifyAPIWrapper.Put<IGendecSettingsWire>(CONFIG_PATH, config, notifications, cancelHook);
};

/**
 * Thunk that runs the initial GET (or any explicit reload) and writes the result into
 * Redux
 */
export const loadGendecConfig = (
  cancelHook?: <T>(p: Promise<T>) => Promise<T>
): ThunkAction<Promise<IGendecSettingsWire | null>, IRootState, object, IAppAction> => {
  return async (dispatch: ThunkDispatch<IRootState, object, IAppAction>): Promise<IGendecSettingsWire | null> => {
    const cfg = await getGendecConfig(cancelHook);
    dispatch(setGendecConfig(cfg));
    return cfg;
  };
};
